// NEW_FILE_JUSTIFIED: New control panel page for superadmin users management — no existing equivalent
import { useState, useCallback } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: convex _generated
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: convex _generated
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { UserFiltersBar } from '@/components/control/UserFiltersBar';
import { UserHierarchyTree } from '@/components/control/UserHierarchyTree';
import { CompanyHierarchyTree } from '@/components/control/CompanyHierarchyTree';
import { BlockConfirmDialog } from '@/components/control/BlockConfirmDialog';
import { Ban, CheckCircle, ChevronDown, ChevronRight } from 'lucide-react';
import type { IUserFilters } from '@/@types/superadmin';

export function UsersManagementPage() {
  const [filters, setFilters] = useState<IUserFilters>({
    search: '',
    orgId: '',
    role: '',
    status: '',
  });
  const [expandedOrg, setExpandedOrg] = useState<string | null>(null);
  const [expandedCompany, setExpandedCompany] = useState<string | null>(null);
  const users = useQuery(api.superadmin.queries.listAllUsers, {
    search: filters.search || undefined,
    orgId: filters.orgId || undefined,
    role: filters.role || undefined,
    status: filters.status || undefined,
  });
  const orgs = useQuery(api.superadmin.queries.listOrgsForFilter);
  const blockUser = useMutation(api.superadmin.mutations.blockUser);
  const unblockUser = useMutation(api.superadmin.mutations.unblockUser);
  const [blockTarget, setBlockTarget] = useState<{
    userId: Id<'users'>;
    name: string;
    isBlocked: boolean;
  } | null>(null);
  const isLoading = users === undefined;
  const handleBlock = useCallback(async () => {
    if (!blockTarget) return;
    try {
      if (blockTarget.isBlocked) {
        await unblockUser({ userId: blockTarget.userId });
        toast.success(`${blockTarget.name} unblocked`);
      } else {
        await blockUser({ userId: blockTarget.userId });
        toast.success(`${blockTarget.name} blocked`);
      }
      setBlockTarget(null);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Operation failed');
    }
  }, [blockTarget, blockUser, unblockUser]);
  const formatDate = (ts?: number) =>
    ts
      ? new Date(ts).toLocaleDateString('en-ZA', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })
      : '—';

  return (
    <div className="flex flex-col gap-0 p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold text-foreground">
          Users {users && <span className="text-muted-foreground text-lg">({users.length})</span>}
        </h1>
      </div>
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <UserFiltersBar filters={filters} onFiltersChange={setFilters} orgs={orgs ?? []} />
        <div className="flex items-center px-6 py-2 border-b border-border bg-muted/30 text-xs font-medium uppercase text-muted-foreground">
          <span className="w-48">Name</span>
          <span className="w-56">Email</span>
          <span className="w-28">Role</span>
          <span className="w-40">Organisation</span>
          <span className="w-36">Company</span>
          <span className="w-24">Status</span>
          <span className="w-28">Last Active</span>
          <span className="w-24 text-right">Actions</span>
        </div>
        <div className="overflow-auto max-h-[60vh]">
          {isLoading && (
            <>
              {[...Array(8)].map((_, i) => (
                <div key={i} className="flex items-center px-6 py-3 border-b border-border gap-4">
                  <Skeleton className="h-4 w-36" />
                  <Skeleton className="h-4 w-44" />
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-24" />
                </div>
              ))}
            </>
          )}
          {!isLoading && users.length === 0 && (
            <div className="px-6 py-12 text-center text-muted-foreground">No users found</div>
          )}
          {!isLoading &&
            users.map(user => (
              <div key={user._id}>
                <div className="flex items-center px-6 py-3 border-b border-border hover:bg-muted/50 transition-colors">
                  <span className="w-48 font-medium text-sm truncate">{user.name ?? '—'}</span>
                  <span className="w-56 text-sm text-muted-foreground truncate">
                    {user.email ?? '—'}
                  </span>
                  <span className="w-28 flex flex-wrap gap-1">
                    {user.orgRole && (
                      <Badge variant="outline" className="text-xs capitalize">
                        {user.orgRole}
                      </Badge>
                    )}
                    {user.companyRoles &&
                      user.companyRoles.length > 0 &&
                      user.companyRoles.map(r => (
                        <Badge key={r} variant="secondary" className="text-xs capitalize">
                          {r}
                        </Badge>
                      ))}
                    {!user.orgRole && (!user.companyRoles || user.companyRoles.length === 0) && (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </span>
                  <span className="w-40 text-sm truncate">
                    {user.orgName ? (
                      <button
                        className="flex items-center gap-1 text-foreground hover:text-foreground/70 hover:underline"
                        onClick={() =>
                          setExpandedOrg(expandedOrg === user.orgId ? null : (user.orgId ?? null))
                        }
                      >
                        {expandedOrg === user.orgId ? (
                          <ChevronDown className="w-3 h-3" />
                        ) : (
                          <ChevronRight className="w-3 h-3" />
                        )}
                        {user.orgName}
                      </button>
                    ) : (
                      '—'
                    )}
                  </span>
                  <span className="w-36 text-sm truncate">
                    {user.companyName && user.companyId ? (
                      <button
                        className="flex items-center gap-1 text-foreground hover:text-foreground/70 hover:underline"
                        onClick={() =>
                          setExpandedCompany(
                            expandedCompany === user.companyId ? null : (user.companyId ?? null)
                          )
                        }
                      >
                        {expandedCompany === user.companyId ? (
                          <ChevronDown className="w-3 h-3" />
                        ) : (
                          <ChevronRight className="w-3 h-3" />
                        )}
                        {user.companyName}
                      </button>
                    ) : (
                      '—'
                    )}
                  </span>
                  <span className="w-24">
                    <Badge
                      variant={user.blockedAt ? 'destructive' : 'secondary'}
                      className="text-xs"
                    >
                      {user.blockedAt ? 'Blocked' : 'Active'}
                    </Badge>
                  </span>
                  <span className="w-28 text-xs text-muted-foreground">
                    {formatDate(user.lastActiveAt)}
                  </span>
                  <span className="w-24 flex justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setBlockTarget({
                          userId: user._id,
                          name: user.name ?? user.email ?? 'User',
                          isBlocked: !!user.blockedAt,
                        })
                      }
                    >
                      {user.blockedAt ? (
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                      ) : (
                        <Ban className="w-4 h-4 text-destructive" />
                      )}
                    </Button>
                  </span>
                </div>
                {expandedOrg === user.orgId && user.orgId && (
                  <UserHierarchyTree orgId={user.orgId} />
                )}
                {expandedCompany === user.companyId && user.companyId && (
                  <CompanyHierarchyTree companyId={user.companyId} />
                )}
              </div>
            ))}
        </div>
      </div>
      {blockTarget && (
        <BlockConfirmDialog
          open={!!blockTarget}
          onOpenChange={open => {
            if (!open) setBlockTarget(null);
          }}
          targetName={blockTarget.name}
          targetType="user"
          isBlocked={blockTarget.isBlocked}
          onConfirm={handleBlock}
          isLoading={false}
        />
      )}
    </div>
  );
}
